'use client';

import { Accordion, AccordionDetails, AccordionSummary, Box } from '@mui/material';
import { CollapseArrowDownOutlined } from '@zeelool/icon';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

interface FaqItem {
  question: string;
  qustion?: string;
  answer: string;
  isShow: boolean;
}

interface FaqData {
  title: string;
  faq_arr?: Array<{
    question: string;
    answer: string;
    qustion?: string;
  }>;
  data_array?: Array<{
    question: string;
    answer: string;
    qustion?: string;
  }>;
}

interface FaqAccordionProps {
  faqData: FaqData;
  className?: string;
  containerClassName?: string;
  titleClassName?: string;
  backgroundColor?: string;
  onFaqClick?: (params: { index: any; question: any; pits_index: number }) => void;
  onExposure?: () => void;
}

export default function FaqAccordion({
  faqData,
  className = '',
  containerClassName = 'px-[14px] py-[32px] lg:px-[100px] xl:px-[224px] xl:py-[50px] 2xl:px-[448px] 2xl:pt-[57px] 2xl:pb-[72px]',
  titleClassName = 'font-bb text-[20px] leading-[24px] text-center xl:text-[36px] xl:leading-[44px] 2xl:text-[48px] 2xl:leading-[58px] text-primary-text border-b border-[#D4D4D4] pb-[21px] xl:pb-[24px] 2xl:pb-[28px]',
  backgroundColor = '',
  onFaqClick,
  onExposure,
}: FaqAccordionProps) {
  const [faqList, setFaqList] = useState<FaqItem[]>([]);
  const [expandedItems, setExpandedItems] = useState<number[]>([]);

  // 使用全局的 useInView 进行曝光检测
  const [faqRef, inView] = useInView({
    threshold: 0.8, // 当FAQ组件10%可见时触发曝光
    triggerOnce: true, // 只触发一次
  });

  // 处理曝光埋点
  useEffect(() => {
    if (inView && onExposure) {
      onExposure();
    }
  }, [inView, onExposure]);

  // 初始化FAQ列表
  useEffect(() => {
    // 兼容其他字段
    // 注意：这里不太好统一，因为是从jsonvalue中配置的数据，部分页面使用是直接传递数据而没有变更字段。
    const _faqList = faqData?.faq_arr || faqData?.data_array;

    if (_faqList) {
      const initialList = _faqList.map((item, index) => ({
        ...item,
        isShow: index === 0, // 默认打开第一个
      }));
      setFaqList(initialList);

      // 如果有数据，默认展开第一个
      if (_faqList.length > 0) {
        setExpandedItems([0]);
      }
    }
  }, [faqData]);

  // 处理展开/收起
  const handleChange = (index: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedItems((prev) => {
      if (isExpanded) {
        return [...prev, index];
      }
      return prev.filter((item) => item !== index);
    });

    setFaqList((prev) => {
      const newList = [...prev];
      newList[index].isShow = isExpanded;
      return newList;
    });

    // 触发点击埋点
    if (onFaqClick) {
      onFaqClick({
        pits_index: index,
        index: undefined,
        question: undefined,
      });
    }
  };

  // 检查是否展开
  const isExpanded = (index: number) => expandedItems.includes(index);

  if (!faqData?.faq_arr?.length && !faqData?.data_array?.length) {
    return null;
  }

  return (
    <Box ref={faqRef} className={`w-full ${backgroundColor} ${className}`}>
      <Box className={containerClassName}>
        <Box>
          <h1 className={titleClassName} dangerouslySetInnerHTML={{ __html: faqData.title }} />

          <Box component="ul" className="space-y-[0px]">
            {faqList.map((item, index) => (
              <Box
                component="li"
                key={index}
                className="border-b border-[#D4D4D4] pb-[16px] lg:pb-[24px]"
              >
                <Accordion
                  expanded={isExpanded(index)}
                  onChange={handleChange(index)}
                  sx={{
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    border: 'none',
                    margin: 0,
                    '&:before': {
                      display: 'none',
                    },
                    '&.Mui-expanded': {
                      margin: 0,
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <CollapseArrowDownOutlined className="text-[#A9A9A9] text-[16px]" />
                    }
                    sx={{
                      padding: {
                        xs: '16px 0 0',
                        lg: '24px 0 0',
                      },
                      minHeight: 'auto',
                      '& .MuiAccordionSummary-content': {
                        margin: '0 !important',
                      },
                      '&.Mui-expanded': {
                        minHeight: 'auto',
                        margin: 0,
                      },
                      '& .MuiAccordionSummary-expandIconWrapper': {
                        transition: 'transform 0.3s ease',
                        transformOrigin: 'center',
                      },
                      '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
                        transform: 'rotate(180deg)',
                      },
                    }}
                  >
                    <h2
                      className="font-bb text-[14px] xl:text-[16px] leading-[17px] xl:leading-[19px] pr-[10px] lg:text-[16px] lg:leading-[19px] 2xl:text-[16px] 2xl:leading-[19px] text-primary-text lg:pr-[16px] 2xl:pr-[16px]"
                      dangerouslySetInnerHTML={{ __html: item.question || item.qustion }}
                    />
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      padding: '10px 0 0',
                      backgroundColor: 'transparent',
                    }}
                  >
                    <p
                      className="font-ir text-[12px] xl:text-[14px] leading-[18px] xl:leading-[18px] pr-[18px] xl:pr-[24px]"
                      dangerouslySetInnerHTML={{ __html: item.answer }}
                    />
                  </AccordionDetails>
                </Accordion>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
